
import { useEffect, useState } from "react";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

type CountdownProps = {
    endDate: string; // Example format: "2024-12-31T23:59:59Z"
    displayStyle?: "card" | "text"; // New prop to control the display style
    inDetail?: boolean; // New prop to indicate if this is in the poll detail view
};

const CountdownTimer = ({ endDate, displayStyle = "card", inDetail = false }: CountdownProps) => {
    const calculateTimeLeft = () => {
        const difference = new Date(endDate).getTime() - new Date().getTime();
        if (difference <= 0) return null;
        return {
            days: Math.floor(difference / (1000 * 60 * 60 * 24)),
            hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
            minutes: Math.floor((difference / (1000 * 60)) % 60),
            seconds: Math.floor((difference / 1000) % 60),
        };
    };

    const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

    useEffect(() => {
        if (!timeLeft) return;

        const timer = setInterval(() => {
            const updatedTimeLeft = calculateTimeLeft();
            if (updatedTimeLeft) {
                setTimeLeft(updatedTimeLeft);
            } else {
                clearInterval(timer);
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [endDate]);

    // Check if the poll has more than 10 days remaining - don't show countdown in that case
    // But only for card view, not for detail page
    if (!inDetail && timeLeft && timeLeft.days >= 10) return null;
    
    if (!timeLeft) return <span className="text-red-500">Poll Ended!</span>;

    // Format time values to have leading zeros
    const formatTimeValue = (value: number): string => {
        return value < 10 ? `0${value}` : `${value}`;
    };

    // Text-only display for PollDetail page
    if (displayStyle === "text") {
        return (
            <span className="text-gray-600 dark:text-gray-300 whitespace-nowrap">
                {timeLeft.days}d {formatTimeValue(timeLeft.hours)}h {formatTimeValue(timeLeft.minutes)}m {formatTimeValue(timeLeft.seconds)}s
            </span>
        );
    }

    // Card display for PollListItem
    return (
        <div className="flex items-center bg-white rounded-md px-3 py-1 shadow-md w-[120px] justify-center" style={{ boxShadow: '0 2px 2px rgba(52, 79, 22, 0.2)' }}>
            <TooltipProvider>
                <div className="flex items-center">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="text-[#6DA135] text-xs font-medium whitespace-nowrap">{timeLeft.days}d</span>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>Days</p>
                        </TooltipContent>
                    </Tooltip>
                    <span className="text-[#6DA135] text-xs font-medium">&nbsp;</span>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="text-[#6DA135] text-xs font-medium whitespace-nowrap">{formatTimeValue(timeLeft.hours)}h</span>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>Hours</p>
                        </TooltipContent>
                    </Tooltip>
                    <span className="text-[#6DA135] text-xs font-medium">&nbsp;</span>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="text-[#6DA135] text-xs font-medium whitespace-nowrap">{formatTimeValue(timeLeft.minutes)}m</span>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>Minutes</p>
                        </TooltipContent>
                    </Tooltip>
                    <span className="text-[#6DA135] text-xs font-medium">&nbsp;</span>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="text-[#6DA135] text-xs font-medium whitespace-nowrap">{formatTimeValue(timeLeft.seconds)}s</span>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>Seconds</p>
                        </TooltipContent>
                    </Tooltip>
                </div>
            </TooltipProvider>
        </div>
    );
};

export default CountdownTimer;
